import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { Toaster, toast } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ConfirmProvider } from "@/hooks/useConfirm";
import AppLayout from "@/components/AppLayout";
import ErrorBoundary from "@/components/ErrorBoundary";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Contacts from "@/pages/Contacts";
import ContactProfile from "@/pages/ContactProfile";
import Tags from "@/pages/Tags";
import Broadcasts from "@/pages/Broadcasts";
import BroadcastEditor from "@/pages/BroadcastEditor";
import BroadcastDetail from "@/pages/BroadcastDetail";
import Inbox from "@/pages/Inbox";
import Templates from "@/pages/Templates";
import Reports from "@/pages/Reports";
import Flows from "@/pages/Flows";
import FlowBuilder from "@/pages/FlowBuilder";
import Settings from "@/pages/Settings";

function App() {
  useEffect(() => {
    // Global safety net: prevent unhandled promise rejections (e.g. axios errors
    // without a .catch()) from triggering React's red error overlay or crashing
    // the app. Surface them as toasts instead.
    const onRejection = (event) => {
      const reason = event?.reason;
      const status = reason?.response?.status;
      const detail = reason?.response?.data?.detail || reason?.message || String(reason);
      // 401 is already handled by the axios interceptor (redirect to /login)
      if (status !== 401) {
        // eslint-disable-next-line no-console
        console.warn("[unhandledrejection]", status, detail, reason);
        toast.error(`Request failed${status ? ` (HTTP ${status})` : ""}: ${detail}`);
      }
      event.preventDefault();
    };
    window.addEventListener("unhandledrejection", onRejection);
    return () => window.removeEventListener("unhandledrejection", onRejection);
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/onboarding" element={<Onboarding />} />

          <Route element={<AppLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/contacts/:id" element={<ContactProfile />} />
            <Route path="/tags" element={<Tags />} />
            <Route path="/broadcasts" element={<Broadcasts />} />
            <Route path="/broadcasts/new" element={<BroadcastEditor />} />
            <Route path="/broadcasts/:id" element={<BroadcastDetail />} />
            <Route path="/broadcasts/:id/edit" element={<BroadcastEditor />} />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/templates" element={<Templates />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/flows" element={<Flows />} />
            <Route path="/flows/:id" element={<FlowBuilder />} />
            <Route path="/settings" element={<Settings />} />
          </Route>
        </Routes>
          </BrowserRouter>
        </ConfirmProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
